/**
 * Signatures in the composer body (DESIGN.md T11, signatures research §5b). The signature is one block
 * `<div data-gx-sig="<id>">…</div>` (editor node `signature`): switching replaces exactly this block and the
 * transport fallback can see that the client already inserted one.
 *
 * Placement like Outlook: a new message gets an empty line for the cursor, then the signature; a reply or forward
 * gets it between the cursor line and the quoted history (`data-gx-quote`).
 */
/** Same marker as the editor node (kept here so this module does not load the editor chunk). */
const SIGNATURE_ATTRIBUTE = 'data-gx-sig';

export interface ComposerSignature {
  id: string;
  /** Rendered HTML (placeholders filled, sanitized by the server). */
  html: string;
}

const QUOTE_START = /<div\b[^>]*\bdata-gx-quote\b/i;
const MARKER = new RegExp(`\\b${SIGNATURE_ATTRIBUTE}="([^"]*)"`, 'i');

/** The marked signature block of `signature`. */
export function signatureBlock({ id, html }: ComposerSignature): string {
  return `<div ${SIGNATURE_ATTRIBUTE}="${id.replace(/[&"<>]/g, '')}">${html}</div>`;
}

/**
 * Editor HTML of a new compose with the signature in its place: above the quoted history when `body` has one,
 * else after an empty cursor line. `body` without a signature is returned unchanged.
 */
export function withSignature(body: string, signature: ComposerSignature | undefined): string {
  if (!signature) return body;
  const block = signatureBlock(signature);
  const quote = QUOTE_START.exec(body);
  if (quote) return `${body.slice(0, quote.index)}${block}${body.slice(quote.index)}`;
  return `${body === '' ? '<p></p>' : body}${block}`;
}

/** Id of the signature block in `body` (e.g. a saved draft), if any. */
export function signatureIdIn(body: string): string | undefined {
  return MARKER.exec(body)?.[1];
}
